import React, { useState } from "react";
import { Box, Link } from "@mui/material";
import { ExtraPaddingLeftStyledCell, ListingCellCenter, ListingCellLeft, ListingCellRight, ListingIconMetric, ListingMetricValue } from "../../../components/commonComponents/TabularLists/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  HtmlTooltip,
  HtmlTooltipDisabled,
  IconContainer,
  IconText,
  StyledStack,
} from "../../../components/commonComponents/StyledComponents/styled";
import ProgressBar from "../../../components/commonComponents/Progress/ProgressBar";
import { Store, Edit, Visibility, ContentCopyTwoTone } from "@mui/icons-material";
import { fetchClipNames, loadClips, manageClipsPaginationCookie, resetClipsData } from "../../../api_calls/clipRequests";
import { fetchCampaignNames, fetchOwnerNames } from "../../../api_calls/campaignRequests";
import { canEditAcrossCompanies } from "../../../api_calls/crossCompanyAccess";
import { tenantRetailerScopeAllowsOperation } from "../../../api_calls/tenantRetailerRecordAccess";
import { fetchCompanyHierarchy } from "../../../api_calls/companyRequests";
import AddNewClip from "../ModalContent/AddClip";
import PlayClip from "../ModalContent/PlayClip";
import { checkUserDetailsFromCookie, checkUserPermissions } from "../../../api_calls/userManagement";
import StoreInfo from "../ModalContent/StoreInfo";
import InterruptedProgress from "../../../components/commonComponents/Progress/InterruptedProgress";
import { fetchStoreNames } from "../../../api_calls/infrastructure";
import { useListingTableController } from "../../../hooks/useListingTableController";
import ListingDataTable from "../../../components/commonComponents/TabularLists/ListingDataTable";
import RetailerIconCount from "../../../components/commonComponents/TabularLists/RetailerIconCount";
import { resetListingCheckData } from "../../../api_calls/listingCheck";
import Confirmation from "../../../components/commonComponents/Confirmation";
import { LISTING_RELOAD, listingTableIsLoading } from "../../../api_calls/listingReload";

const ClipsListing = () => {
  const campaignDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  let listItems = typeof campaignDetails.clips_list !== "undefined"
    ? campaignDetails.clips_list
    : [];
  let clipsListitems = listItems.length !== 0 ? listItems : [];

  const [openModal, setOpenModal] = useState(false);
  const [clipInfo, setClipInfo] = useState(false);
  const [playClipModal, setPlayClip] = useState(false);
  const [showStoresModal, setShowStores] = useState(false);
  const [showCloneConfirmation, setShowCloneConfirmation] = useState(false);
  const [cloneClipInfo, setCloneClipInfo] = useState(null);
  const [cloneModal, setCloneModal] = useState(false);

  const checkUserDetails = checkUserDetailsFromCookie();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }
  const [userInfo, setUserInfo] = useState(userDetailObj);
  const [listLoaded, setListLoaded] = useState(false);

  const closeModal = () => {
    resetClipsData();
    resetListingCheckData();
    setOpenModal(false);
  };

  // ,
  // Hierarchy lookup runs in parallel with the clips list request.
  // Page spinner is held by the listing controller until this AND the list request finish.
  const initClipsLookups = React.useCallback(async () => {
    await Promise.all([
      // loadClips(),
      // fetchClipNames(),
      // fetchCampaignNames(),
      // fetchStoreNames(),
      // fetchOwnerNames("list-clips"),
      fetchCompanyHierarchy({ viewCampaignActive: true }),
    ]);
    // scroll to the last position where user was before refreshing the page
    // scrollTableListings("clips_pagination");
  }, []);

  const { pagination, onPageChange, onPageSizeChange } = useListingTableController({
    paginationReduxKey: "clips_pagination",
    paginationCookieKey: "clips_pagination",
    managePaginationCookie: manageClipsPaginationCookie,
    loadPage: (pageNumber, pageSize) => loadClips(pageNumber, pageSize).then(() => {
      setListLoaded(true);
    }),

    filtersReduxKey: "clip_list_filters",
    filtersCookieKey: "clip_list_filters",
    filterAppliedReduxKey: "clips_list_filter_applied",

    onInit: initClipsLookups,
    onReset: resetClipsData,
    deletePaginationScope: "clips",
    dataLoadedFlag: listLoaded,
    listingReloadKey: LISTING_RELOAD.clips,
  });

  const columns = React.useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        cellClassName: "text-wrapper wrapLargeText",
        render: (clip) => (
          <Link
            href="#"
            underline="none"
            aria-label={"clip record for " + clip.name}
            title={clip.name}
            sx={{ color: "#AF3241" }}
            onClick={(e) => {
              e.preventDefault();
              setClipInfo(clip);
              setPlayClip(true);
            }}
          >
            {clip.name}
          </Link>
        ),
      },
      {
        id: "campaign",
        header: "Campaign",
        cellClassName: "text-wrapper wrapLargeText",
        render: (clip) => (
          <StyledStack direction="row">
            {clip.campaign}
          </StyledStack>
        ),
      },
      {
        id: "progress",
        header: "Progress",
        headerAlign: "center",
        align: "center",
        render: (clip) =>
          clip.active === true ? (
            <ProgressBar
              startDate={clip.start_date}
              endDate={clip.end_date}
            />
          ) : (
            <InterruptedProgress
              startDate={clip.start_date}
              endDate={clip.end_date}
            />
          ),
      },
      {
        id: "owner",
        header: "Owner",
        headerAlign: "center",
        align: "center",
        cellClassName: "text-wrapper wrapLargeText",
        render: (clip) => (
          <ListingCellCenter>{clip.clip_company_name}</ListingCellCenter>
        ),
      },
      // Retailer column only for cyreen / tenant (same user types as OwnerField)
      ...(["cyreen", "tenant"].includes(userInfo.user_type)
        ? [
            {
              id: "retailer",
              header: "Retailer",
              headerAlign: "center",
              align: "center",
              cellClassName: "text-wrapper wrapLargeText",
              render: (clip) => (
                <ListingCellCenter>
                  <RetailerIconCount names={clip.retailer_names} />
                </ListingCellCenter>
              ),
            },
          ]
        : []),
      {
        id: "stores",
        header: "Stores",
        headerAlign: "center",
        align: "center",
        render: (clip) => (
          <ListingCellCenter>
            <Link
              href="#"
              underline="none"
              sx={{ color: "#AF3241" }}
              onClick={(e) => {
                e.preventDefault();
                setClipInfo(clip);
                setShowStores(true);
              }}
            >
              <ListingIconMetric>
                <Store sx={{ color: "#AF3241" }} />
                <ListingMetricValue>
                  <IconText variant="body2">
                    {clip.stores}
                  </IconText>
                </ListingMetricValue>

              </ListingIconMetric>
            </Link>
          </ListingCellCenter>
        ),
      },
      {
        id: "impressions",
        header: "Impressions",
        headerAlign: "center",
        align: "center",
        render: (clip) => (
          <ListingCellCenter>
            <HtmlTooltip title="Impressions">
              <ListingIconMetric>
                <Visibility sx={{ color: "#AF3241" }} />
                <ListingMetricValue>
                  {clip.unlimited === true ? (
                    <IconText variant="body2">Unlimited</IconText>
                  ) : (
                    <IconText variant="body2">
                      {clip.campaign_contacts}
                    </IconText>
                  )}
                </ListingMetricValue>

              </ListingIconMetric>
            </HtmlTooltip>
          </ListingCellCenter>
        ),
      },
      {
        id: "action",
        hideWhenEmpty: true,
        header: "Action",
        headerAlign: "center",
        align: "center",
        render: (clip) => {
          // Edit and clone share one access rule: own company, or a user who can edit across companies.
          // Store-based access (another company's clip in the workspace's stores) grants neither.
          const clipAccessByWorkspace =
            (typeof userInfo.current_workspace !== "undefined" &&
              userInfo.current_workspace.id === clip.clip_company_id) ||
            canEditAcrossCompanies(userInfo.user_type) === true;
          const isOwnCompanyClip =
            clipAccessByWorkspace === true &&
            // retailer_names is the list shown in the Retailer column.
            tenantRetailerScopeAllowsOperation(userInfo.user_type, clip.retailer_names, campaignDetails) === true;
          const isEditable = permissionScope.clip_list_can_edit === true && isOwnCompanyClip;
          const isCloneable = isEditable && !clip.campaign_finished;

          return (
            <ListingCellCenter>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                {/* ------- edit visual ------- */}
                {permissionScope.clip_list_can_edit === true ? (
                  isOwnCompanyClip ? (
                    <HtmlTooltip title="Edit">
                      <a
                        href="#editClip"
                        aria-label={"edit visual " + clip.name}
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenModal(true);
                          setClipInfo(clip);
                        }}
                      >
                        <Edit sx={{ color: "#AF3241" }} />
                      </a>
                    </HtmlTooltip>
                  ) : (
                    <HtmlTooltipDisabled title="Not Available">
                      <Edit sx={{ color: "#E6E6E6" }} />
                    </HtmlTooltipDisabled>
                  )
                ) : (
                  <HtmlTooltipDisabled title="No Access">
                    <Edit sx={{ color: "#E6E6E6" }} />
                  </HtmlTooltipDisabled>
                )}

                {/* ------- clone visual ------- */}
                {isCloneable ? (
                  <HtmlTooltip title="Clone">
                    <a
                      href="#cloneClip"
                      aria-label={"clone visual " + clip.name}
                      onClick={(e) => {
                        e.preventDefault();
                        setCloneClipInfo(clip);
                        setShowCloneConfirmation(true);
                      }}
                    >
                      <ContentCopyTwoTone sx={{ color: "#AF3241" }} />
                    </a>
                  </HtmlTooltip>
                ) : (
                  <HtmlTooltipDisabled
                    title={
                      isEditable && clip.campaign_finished
                        ? "Campaign ended"
                        : "Not Available"
                    }
                  >
                    <span>
                      <ContentCopyTwoTone sx={{ color: "#E6E6E6" }} />
                    </span>
                  </HtmlTooltipDisabled>
                )}
              </Box>
            </ListingCellCenter>
          );
        },
      },
    ],
    [userInfo.user_type, permissionScope.clip_list_can_edit]
  );


  return (
    <Box>
      {/* ---------- load listing table --------- */}
      <ListingDataTable
        columns={columns}
        rows={clipsListitems}
        rowKey={(clip, idx) => `${idx}_campaign_row`}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        // Table Loading… is per-list; opening New Clip uses the page spinner only.
        loading={listingTableIsLoading(campaignDetails, LISTING_RELOAD.clips, campaignDetails.clips_list)}
      />

      {/* ------- edit clip modal ---------- */}
      {openModal && (
        <AddNewClip
          open={openModal}
          onClose={closeModal}
          size={"md"}
          closeModal={closeModal}
          usedFor={"update"}
          clipInfo={clipInfo}
          setClipInfo={setClipInfo}
        />
      )}
      {/* ------ play clip ------ */}
      {playClipModal === true && (
        <PlayClip
          open={playClipModal}
          onClose={() => {
            setPlayClip(false);
          }}
          size={"md"}
          clipInfo={clipInfo}
          closeModal={() => {
            setPlayClip(false);
          }}
        />
      )}
      {/* ------- store names info ------ */}
      {showStoresModal === true && (
        <StoreInfo
          open={showStoresModal}
          onClose={() => {
            setShowStores(false);
          }}
          size={"md"}
          storeInfo={clipInfo}
          usedFor={"Visual"}
          dialogTitle={"Visual Stores"}
          closeModal={() => {
            setShowStores(false);
          }}
        />
      )}
      {/* ------- clone confirmation ------- */}
      {showCloneConfirmation === true && (
        <Confirmation
          showConfirmationPopup={showCloneConfirmation}
          closePopup={() => {
            setShowCloneConfirmation(false);
          }}
          confirmationActionCall={() => {
            resetClipsData();
            resetListingCheckData();
            setCloneModal(true);
          }}
          confirmationMessage={
            "You are about to clone an existing contact-based visual. Please review the copied information carefully, as some details may differ from the original."
          }
          confirmationTitle={"Confirmation"}
          confirmText={"Ok"}
          denyText={"Cancel"}
        />
      )}
      {/* ------- clone visual modal ------- */}
      {cloneModal === true && cloneClipInfo !== null && (
        <AddNewClip
          open={cloneModal}
          onClose={() => {
            resetClipsData();
            resetListingCheckData();
            setCloneModal(false);
            setCloneClipInfo(null);
          }}
          size={"md"}
          closeModal={() => {
            resetClipsData();
            resetListingCheckData();
            setCloneModal(false);
            setCloneClipInfo(null);
            loadClips();
          }}
          usedFor={"add"}
          cloneSource={cloneClipInfo}
        />
      )}
    </Box>
  )

};

export default ClipsListing;
